import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { AuthUser } from "../app-home/_objects_home";
import { AuthContext } from "../auth-context";
import { RequestContext } from "../request-context";
import { criticityToCSS } from "../utils/criticity";
import { fetchJSON, requestGet, requireResult } from "../utils/network";
import ActionButton from "../widget/action-button";
import ButtonsGroup from "../widget/buttons-group";
import { Modes } from "../widget/io-abstract";
import IOBoolean from "../widget/io-boolean";
import IOPanel from "../widget/io-panel";
import IOString from "../widget/io-string";
import type { User } from "./_objects-users";

export default function PageUser(props: {
  profile?: boolean;
}): React.ReactNode {
  const params = useParams();
  const requestContext = useContext(RequestContext)!;
  const navigate = useNavigate();
  const [user, userUpdated] = useState<User>({} as User);
  const authenticationContext = useContext(AuthContext)!;

  const id = props.profile
    ? authenticationContext.authenticatedData?.user.id
    : params["id"];

  const addMode = id === "add";

  // Effect: get from server
  useEffect(() => {
    if (!addMode) {
      requestContext
        .fetchJSONAndMessage<User>(
          `Loading #${id}`,
          requestGet(`/api/user/${id}`)
        )
        .then(requireResult)
        .then((user) => userUpdated(user))
        .catch(() => {});
    }
  }, [id]);

  const goIndex = () => (props.profile ? navigate("/home") : navigate("/user"));

  if (!addMode && !user.id) {
    return <></>;
  }

  return (
    <>
      <ButtonsGroup>
        <ActionButton onOk={goIndex} action="Go back to index" />
      </ButtonsGroup>
      <IOPanel<User>
        apiRootUrl="/api/user"
        edit
        type="user"
        file={user || ({} as User)}
        restrictedForWrite={props.profile ? "profile" : "manage_users"}
        restrictedForUnlock=""
        onCreated={(data) =>
          navigate(`/user/${data.id}/password?redirect=/user`)
        }
        onUpdated={async () => {
          if (props.profile) {
            await fetchJSON<AuthUser>(requestGet("/api/auth/settings")).then(
              (authenticatedData) =>
                authenticationContext.setAuthenticatedData(authenticatedData)
            );
          }
          goIndex();
        }}
        onDeleted={goIndex}
        onEdit={(edit) => {
          if (!edit) {
            goIndex();
          }
        }}
        header={<span>{user.name}</span>}
      >
        {user.has_password ? (
          <>
            {user.profile_criticity == "OK" || (
              <div
                className={`alert alert-${criticityToCSS(user.profile_criticity)}`}
              >
                These data are too old. Please verify the data present on this
                page and SAVE it.
                <br />
                If the data is already good, please just SAVE it to dismiss this
                message.
              </div>
            )}
            {props.profile && user.password_criticity != "OK" && (
              <div
                className={`alert alert-${criticityToCSS(user.password_criticity)}`}
              >
                Your password is too old. Please change it.
              </div>
            )}
          </>
        ) : (
          <div className="alert alert-warning">
            This user has no password, so s.he won't be able to log in
          </div>
        )}
        <ButtonsGroup>
          {!addMode && (
            <ActionButton
              action="Change password"
              style="Alternate"
              linkTo={
                props.profile
                  ? `/profile/password`
                  : `/user/${user.id}/password`
              }
            />
          )}
        </ButtonsGroup>
        <IOString
          name="username"
          value={user.username}
          mode={addMode ? Modes.input : Modes.output}
          validation={(value) => {
            value = value ?? "";
            if (value.toLocaleLowerCase() !== value || value.includes(" ")) {
              return "Username need to be in lowercase, without spaces";
            }
            return "";
          }}
        />
        <IOBoolean
          name="hasAPassword"
          value={user.has_password}
          mode={Modes.output}
        />
        <IOString name="name" value={user.name} />
        <IOString name="email" value={user.email} />
        <IOBoolean
          name="in_examiner_list"
          value={user.in_examiner_list}
          required
        />
      </IOPanel>
    </>
  );
}
